import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, User, Phone, MapPin, Hash, FileText, ShoppingBag } from 'lucide-react';
import clsx from 'clsx';
import { usePlaceOrder } from '../../services/productService';
import { formatPrice } from '../../lib/utils';

/**
 * Order modal — full-screen on mobile, centered on desktop.
 * Collects name, phone, address, quantity, note. Sends to Edge Function.
 * Detects QR code source from URL parameters.
 */
export function OrderModal({ product, isOpen, onClose }) {
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    quantity: 1,
    note: '',
  });
  const [errors, setErrors] = useState({});
  const placeOrder = usePlaceOrder();

  // Detect QR code from URL
  const qrCodeId = new URLSearchParams(window.location.search).get('qr');

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = 'Name is required';
    if (!form.customer_phone.trim()) e.customer_phone = 'Phone is required';
    else if (!/^\+?\d{9,15}$/.test(form.customer_phone.replace(/[\s-]/g, '')))
      e.customer_phone = 'Enter a valid phone (e.g. 0712345678)';
    if (!form.customer_address.trim()) e.customer_address = 'Delivery address is required';
    if (!form.quantity || form.quantity < 1) e.quantity = 'At least 1';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const result = await placeOrder.mutateAsync({
        product_id: product.id,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_address: form.customer_address.trim(),
        quantity: Number(form.quantity),
        note: form.note.trim() || null,
        qr_code_id: qrCodeId || null, // Track QR code source
      });

      // Redirect after order
      if (result.whatsapp_link) {
        window.open(result.whatsapp_link, '_blank');
      }
      onClose();
      // Reset form
      setForm({ customer_name: '', customer_phone: '', customer_address: '', quantity: 1, note: '' });
    } catch (err) {
      setErrors({ submit: err.message });
    }
  };

  const totalPrice = product ? product.price * form.quantity : 0;

  return (
    <AnimatePresence>
      {isOpen && product && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-brand-dark/60 backdrop-blur-sm z-50"
          />

          {/* Modal — centered with flex */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <motion.div
              initial={{ y: 30, scale: 0.95 }}
              animate={{ y: 0, scale: 1 }}
              exit={{ y: 30, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-brand-white w-full max-w-lg max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            >
            {/* Header */}
            <div className="bg-brand-white border-b border-ui-border px-5 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-brand-dark">Place Order</h2>
                <p className="text-xs text-brand-gray mt-0.5">{product.title}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-brand-gray hover:bg-brand-light transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Product summary */}
              <div className="flex items-center gap-3 p-3 bg-brand-light rounded-xl">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-brand-dark truncate">{product.title}</p>
                  <p className="text-xs text-brand-gray">
                    {formatPrice(product.price)} each
                  </p>
                </div>
                <p className="text-lg font-extrabold text-brand-red">
                  {formatPrice(totalPrice)}
                </p>
              </div>

              {/* Name */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-brand-dark mb-1.5">
                  <User size={14} className="text-brand-gray" />
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(e) => updateField('customer_name', e.target.value)}
                  placeholder="John Doe"
                  className={clsx(
                    'w-full px-4 py-3 border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 transition-colors',
                    errors.customer_name
                      ? 'border-error focus:ring-error/30'
                      : 'border-ui-border focus:ring-brand-red/30 focus:border-brand-red'
                  )}
                />
                {errors.customer_name && (
                  <p className="text-xs text-error mt-1">{errors.customer_name}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-brand-dark mb-1.5">
                  <Phone size={14} className="text-brand-gray" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={form.customer_phone}
                  onChange={(e) => updateField('customer_phone', e.target.value)}
                  placeholder="0712 345 678"
                  className={clsx(
                    'w-full px-4 py-3 border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 transition-colors',
                    errors.customer_phone
                      ? 'border-error focus:ring-error/30'
                      : 'border-ui-border focus:ring-brand-red/30 focus:border-brand-red'
                  )}
                />
                <p className="text-xs text-brand-gray mt-1">
                  Format: 0712345678 or +254712345678
                </p>
                {errors.customer_phone && (
                  <p className="text-xs text-error mt-1">{errors.customer_phone}</p>
                )}
              </div>

              {/* Address */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-brand-dark mb-1.5">
                  <MapPin size={14} className="text-brand-gray" />
                  Delivery Address
                </label>
                <input
                  type="text"
                  value={form.customer_address}
                  onChange={(e) => updateField('customer_address', e.target.value)}
                  placeholder="e.g. Westlands, Nairobi"
                  className={clsx(
                    'w-full px-4 py-3 border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 transition-colors',
                    errors.customer_address
                      ? 'border-error focus:ring-error/30'
                      : 'border-ui-border focus:ring-brand-red/30 focus:border-brand-red'
                  )}
                />
                {errors.customer_address && (
                  <p className="text-xs text-error mt-1">{errors.customer_address}</p>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-brand-dark mb-1.5">
                  <Hash size={14} className="text-brand-gray" />
                  Quantity
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateField('quantity', Math.max(1, form.quantity - 1))}
                    className="w-10 h-10 rounded-xl border border-ui-border text-brand-dark font-bold hover:bg-brand-light transition-colors text-lg"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={(e) => updateField('quantity', Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 text-center px-2 py-2.5 border border-ui-border rounded-xl text-sm font-bold text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-red/30"
                  />
                  <button
                    type="button"
                    onClick={() => updateField('quantity', form.quantity + 1)}
                    className="w-10 h-10 rounded-xl border border-ui-border text-brand-dark font-bold hover:bg-brand-light transition-colors text-lg"
                  >
                    +
                  </button>
                </div>
                {errors.quantity && (
                  <p className="text-xs text-error mt-1">{errors.quantity}</p>
                )}
              </div>

              {/* Note */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-brand-dark mb-1.5">
                  <FileText size={14} className="text-brand-gray" />
                  Note (optional)
                </label>
                <textarea
                  value={form.note}
                  onChange={(e) => updateField('note', e.target.value)}
                  placeholder="Any special requests..."
                  rows={3}
                  className="w-full px-4 py-3 border border-ui-border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-colors resize-none"
                />
              </div>

              {/* Error message */}
              {errors.submit && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-xl">
                  <p className="text-sm text-error font-medium">{errors.submit}</p>
                </div>
              )}

              {/* Submit */}
              <div className="pt-2 pb-2">
                <button
                  type="submit"
                  disabled={placeOrder.isPending}
                  className={clsx(
                    'w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-base transition-colors shadow-lg',
                    placeOrder.isPending
                      ? 'bg-success/60 text-brand-white cursor-not-allowed'
                      : 'bg-success text-brand-white hover:bg-green-700'
                  )}
                >
                  {placeOrder.isPending ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingBag size={18} />
                      Place Order — {formatPrice(totalPrice)}
                    </>
                  )}
                </button>
              </div>
            </form>            </motion.div>          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
